import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "../lib/tenant-context";
import { tenantFetch } from "../lib/api-client";
import { RequirePlan } from "../components/RouteGuard";
import { Safe2 } from "@solar-icons/react";
import {
  Wallet,
  TrendingDown,
  AlertTriangle,
  Plus,
  RefreshCw,
  Check,
  Clock,
  DollarSign,
  Bell,
  ChevronRight,
  X,
  Zap,
  Link as LinkIcon,
  ArrowUpRight,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/treasury")({
  component: () => (
    <RequirePlan required="starter" featureName="Treasury" icon={<Safe2 size={64} weight="LineDuotone" />}>
      <Treasury />
    </RequirePlan>
  ),
});

// Demo data for treasury when API is unavailable
function getTenantDemoTreasury(tenantId: string): TreasuryData {
  const baseData: TreasuryData = {
    treasury: {
      id: `trs_${tenantId}`,
      tenant_id: tenantId,
      currency: "USD",
      is_active: true,
      settings: {
        low_balance_threshold_cents: 100000,
        auto_reload_enabled: false,
        auto_reload_amount_cents: null,
        auto_reload_trigger_cents: null,
      },
    },
    balance: {
      treasury_id: `trs_${tenantId}`,
      balance_cents: 0,
      available_cents: 0,
      reserved_cents: 0,
      lifetime_deposited: 0,
      lifetime_spent: 0,
      is_low_balance: false,
      deposits_last_30_days: 0,
      pending_reservations: 0,
      unacknowledged_alerts: 0,
    },
    recent_deposits: [],
    alerts: [],
  };

  switch (tenantId) {
    case "arcteryx":
      return {
        ...baseData,
        balance: {
          ...baseData.balance,
          balance_cents: 2450000,
          available_cents: 2250000,
          reserved_cents: 200000,
          lifetime_deposited: 5000000,
          lifetime_spent: 2550000,
          is_low_balance: false,
          deposits_last_30_days: 500000,
          pending_reservations: 12,
          unacknowledged_alerts: 0,
        },
        recent_deposits: [
          {
            id: "dep_arc_1",
            amount_cents: 500000,
            currency: "USD",
            payment_method: "wire_transfer",
            payment_reference: "WT-2024-001",
            status: "completed",
            notes: "Q4 budget allocation",
            initiated_by: "admin@arcteryx.com",
            created_at: new Date(
              Date.now() - 7 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          },
          {
            id: "dep_arc_2",
            amount_cents: 250000,
            currency: "USD",
            payment_method: "credit_card",
            payment_reference: null,
            status: "completed",
            notes: null,
            initiated_by: "admin@arcteryx.com",
            created_at: new Date(
              Date.now() - 30 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          },
        ],
        alerts: [],
      };
    case "lululemon":
      return {
        ...baseData,
        balance: {
          ...baseData.balance,
          balance_cents: 1875000,
          available_cents: 1675000,
          reserved_cents: 200000,
          lifetime_deposited: 4500000,
          lifetime_spent: 2625000,
          is_low_balance: false,
          deposits_last_30_days: 375000,
          pending_reservations: 18,
          unacknowledged_alerts: 0,
        },
        recent_deposits: [
          {
            id: "dep_lulu_1",
            amount_cents: 375000,
            currency: "USD",
            payment_method: "wire_transfer",
            payment_reference: "WT-LULU-2024-012",
            status: "completed",
            notes: "Like New program funding",
            initiated_by: "treasury@lululemon.com",
            created_at: new Date(
              Date.now() - 5 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          },
        ],
        alerts: [],
      };
    case "nespresso":
      return {
        ...baseData,
        balance: {
          ...baseData.balance,
          balance_cents: 890000,
          available_cents: 840000,
          reserved_cents: 50000,
          lifetime_deposited: 2000000,
          lifetime_spent: 1110000,
          is_low_balance: false,
          deposits_last_30_days: 200000,
          pending_reservations: 8,
          unacknowledged_alerts: 0,
        },
        recent_deposits: [
          {
            id: "dep_nes_1",
            amount_cents: 200000,
            currency: "USD",
            payment_method: "credit_card",
            payment_reference: null,
            status: "completed",
            notes: "Recycling rewards fund",
            initiated_by: "ops@nespresso.com",
            created_at: new Date(
              Date.now() - 14 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          },
        ],
        alerts: [],
      };
    case "lego":
      return {
        ...baseData,
        balance: {
          ...baseData.balance,
          balance_cents: 3250000,
          available_cents: 3000000,
          reserved_cents: 250000,
          lifetime_deposited: 6000000,
          lifetime_spent: 2750000,
          is_low_balance: false,
          deposits_last_30_days: 750000,
          pending_reservations: 25,
          unacknowledged_alerts: 0,
        },
        recent_deposits: [
          {
            id: "dep_lego_1",
            amount_cents: 750000,
            currency: "USD",
            payment_method: "wire_transfer",
            payment_reference: "WT-LEGO-2024-003",
            status: "completed",
            notes: "Brick Recycler program expansion",
            initiated_by: "sustainability@lego.com",
            created_at: new Date(
              Date.now() - 3 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          },
          {
            id: "dep_lego_2",
            amount_cents: 500000,
            currency: "USD",
            payment_method: "wire_transfer",
            payment_reference: "WT-LEGO-2024-002",
            status: "completed",
            notes: "Q4 sustainability budget",
            initiated_by: "sustainability@lego.com",
            created_at: new Date(
              Date.now() - 21 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          },
        ],
        alerts: [],
      };
    case "nike":
      return {
        ...baseData,
        balance: {
          ...baseData.balance,
          balance_cents: 2894000,
          available_cents: 2644000,
          reserved_cents: 250000,
          lifetime_deposited: 5500000,
          lifetime_spent: 2606000,
          is_low_balance: false,
          deposits_last_30_days: 600000,
          pending_reservations: 15,
          unacknowledged_alerts: 0,
        },
        recent_deposits: [
          {
            id: "dep_nike_1",
            amount_cents: 600000,
            currency: "USD",
            payment_method: "wire_transfer",
            payment_reference: "WT-NIKE-2024-015",
            status: "completed",
            notes: "Nike Refurbished program Q4 funding",
            initiated_by: "treasury@nike.com",
            created_at: new Date(
              Date.now() - 4 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          },
          {
            id: "dep_nike_2",
            amount_cents: 400000,
            currency: "USD",
            payment_method: "wire_transfer",
            payment_reference: "WT-NIKE-2024-014",
            status: "completed",
            notes: "Jordan Collection Buyback expansion",
            initiated_by: "treasury@nike.com",
            created_at: new Date(
              Date.now() - 18 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          },
        ],
        alerts: [],
      };
    default:
      return baseData;
  }
}

interface TreasuryData {
  treasury: {
    id: string;
    tenant_id: string;
    currency: string;
    is_active: boolean;
    settings: {
      low_balance_threshold_cents: number;
      auto_reload_enabled: boolean;
      auto_reload_amount_cents: number | null;
      auto_reload_trigger_cents: number | null;
    };
  };
  balance: {
    treasury_id: string;
    balance_cents: number;
    available_cents: number;
    reserved_cents: number;
    lifetime_deposited: number;
    lifetime_spent: number;
    is_low_balance: boolean;
    deposits_last_30_days: number;
    pending_reservations: number;
    unacknowledged_alerts: number;
  };
  recent_deposits: Deposit[];
  alerts: Alert[];
}

interface Deposit {
  id: string;
  amount_cents: number;
  currency: string;
  payment_method: string;
  payment_reference: string | null;
  status: "pending" | "processing" | "completed" | "failed" | "refunded";
  notes: string | null;
  initiated_by: string;
  created_at: string | number;
  confirmed_at?: string | number | null;
}

interface Alert {
  id: string;
  type: string;
  message: string;
  balance_at_alert: number | null;
  acknowledged: boolean;
  created_at: number;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function formatDateTime(timestamp: string | number): string {
  const date =
    typeof timestamp === "string"
      ? new Date(timestamp)
      : new Date(timestamp * 1000);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Deposit Modal Component
function DepositModal({
  isOpen,
  onClose,
  onDeposit,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onDeposit: (data: {
    amount_cents: number;
    payment_method: string;
    payment_reference?: string;
    notes?: string;
  }) => void;
  isLoading: boolean;
}) {
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("wire_transfer");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const amountCents = Math.round(parseFloat(amount) * 100);
    if (isNaN(amountCents) || amountCents <= 0) return;

    onDeposit({
      amount_cents: amountCents,
      payment_method: paymentMethod,
      payment_reference: reference || undefined,
      notes: notes || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white max-w-md w-full p-6 animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-penta-black">
            Record Deposit
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-penta-grey-light rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-penta-grey-medium mb-1">
              Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-penta-grey-medium">
                $
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-2 border border-penta-grey-medium/20"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-penta-grey-medium mb-1">
              Payment Method
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-4 py-2 border border-penta-grey-medium/20"
            >
              <option value="wire_transfer">Wire Transfer</option>
              <option value="ach">ACH</option>
              <option value="card">Card</option>
              <option value="manual_adjustment">Manual Adjustment</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-penta-grey-medium mb-1">
              Reference (optional)
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Wire reference, transaction ID, etc."
              className="w-full px-4 py-2 border border-penta-grey-medium/20"
            />
          </div>

          <div>
            <label className="block text-sm text-penta-grey-medium mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes..."
              className="w-full px-4 py-2 border border-penta-grey-medium/20 resize-none"
              rows={2}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-penta-grey-medium/20 text-penta-grey-medium rounded-full hover:bg-penta-grey-light"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !amount}
              className="flex-1 px-4 py-2 bg-penta-mint text-penta-black rounded-full font-medium disabled:opacity-50"
            >
              {isLoading ? "Recording..." : "Record Deposit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// USDC Tab Component
function UsdcTab({
  data,
  onToggle,
  isToggling,
}: {
  data: UsdcTreasuryData | null;
  onToggle: (action: "enable" | "disable") => void;
  isToggling: boolean;
}) {
  const isEnabled = data?.usdc_enabled || false;
  const walletAddress = data?.usdc_wallet_address || null;

  return (
    <div className="space-y-6">
      {/* Enable/Disable Toggle */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-body font-medium text-penta-black">
              USDC Agent Payments
            </h2>
            <p className="text-sm text-penta-grey-medium mt-1">
              Enable x402 protocol payments for your brand agent. Pay per API
              call with USDC on Base.
            </p>
          </div>
          <button
            onClick={() => onToggle(isEnabled ? "disable" : "enable")}
            disabled={isToggling}
            className="flex items-center gap-2 text-penta-black"
          >
            {isEnabled ? (
              <ToggleRight size={32} className="text-penta-mint" />
            ) : (
              <ToggleLeft size={32} className="text-penta-grey-medium" />
            )}
          </button>
        </div>
      </div>

      {isEnabled && (
        <>
          {/* Balance Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-penta-grey-medium">
                  USDC Balance
                </span>
                <Zap className="text-penta-mint" size={20} />
              </div>
              <p className="text-2xl font-light text-penta-black">
                ${data?.usdc_balance_usd || "0.00"}
              </p>
              <p className="text-xs text-penta-grey-medium mt-1">
                USDC on Base
              </p>
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-penta-grey-medium">
                  Total x402 Calls
                </span>
                <Zap className="text-penta-blue" size={20} />
              </div>
              <p className="text-2xl font-light text-penta-black">
                {(data?.x402_total_calls || 0).toLocaleString()}
              </p>
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-penta-grey-medium">
                  Total Spent
                </span>
                <TrendingDown className="text-penta-coral" size={20} />
              </div>
              <p className="text-2xl font-light text-penta-black">
                ${data?.x402_total_spent_usd || "0.00"}
              </p>
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-penta-grey-medium">
                  Last Activity
                </span>
                <Clock className="text-penta-grey-medium" size={20} />
              </div>
              <p className="text-2xl font-light text-penta-black">
                {data?.x402_last_call_at
                  ? formatDateTime(data.x402_last_call_at)
                  : "None"}
              </p>
            </div>
          </div>

          {/* Wallet Address & Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <h2 className="text-body font-medium text-penta-black mb-4">
                Wallet Address
              </h2>
              {walletAddress ? (
                <div className="flex items-center gap-2 p-3 bg-penta-grey-light/50">
                  <LinkIcon
                    size={16}
                    className="text-penta-grey-medium shrink-0"
                  />
                  <code className="text-sm text-penta-black font-mono break-all">
                    {walletAddress}
                  </code>
                </div>
              ) : (
                <p className="text-sm text-penta-grey-medium">
                  Wallet address will be generated when USDC is enabled.
                </p>
              )}
              <p className="text-xs text-penta-grey-medium mt-3">
                Fund this address with USDC on Base to enable agent payments.
              </p>
            </div>

            <div className="card">
              <h2 className="text-body font-medium text-penta-black mb-4">
                Pricing
              </h2>
              <div className="space-y-2">
                {[
                  {
                    endpoint: "/assess",
                    price: "$0.25",
                    desc: "Full takeback assessment",
                  },
                  {
                    endpoint: "/identify",
                    price: "$0.05",
                    desc: "Product identification",
                  },
                  {
                    endpoint: "/valuate",
                    price: "$0.10",
                    desc: "Price estimation",
                  },
                  {
                    endpoint: "/search",
                    price: "$0.01",
                    desc: "Semantic search",
                  },
                ].map((item) => (
                  <div
                    key={item.endpoint}
                    className="flex items-center justify-between p-2 bg-penta-grey-light/50"
                  >
                    <div>
                      <span className="text-sm font-mono text-penta-black">
                        {item.endpoint}
                      </span>
                      <span className="text-xs text-penta-grey-medium ml-2">
                        {item.desc}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-penta-black">
                      {item.price}
                    </span>
                  </div>
                ))}
              </div>
              <a
                href="/x402-usage"
                className="inline-flex items-center gap-1 text-sm text-penta-mint hover:underline mt-4"
              >
                View detailed usage <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </>
      )}

      {!isEnabled && (
        <div className="card text-center py-12">
          <Zap
            className="mx-auto mb-3 text-penta-grey-medium"
            size={40}
            strokeWidth={1}
          />
          <h3 className="text-body font-medium text-penta-black mb-2">
            Agent-native payments with x402
          </h3>
          <p className="text-sm text-penta-grey-medium max-w-md mx-auto mb-4">
            Enable USDC treasury to let your brand agent call Thingdom AI
            services (product identification, valuation, takeback assessment)
            and pay per call via the x402 protocol. Works alongside your
            existing invoicing.
          </p>
          <button
            onClick={() => onToggle("enable")}
            disabled={isToggling}
            className="px-6 py-2 bg-penta-mint text-penta-black rounded-full font-mono text-sm uppercase tracking-wider disabled:opacity-50"
          >
            {isToggling ? "Enabling..." : "Enable USDC Treasury"}
          </button>
        </div>
      )}
    </div>
  );
}

interface UsdcTreasuryData {
  usdc_enabled: boolean;
  usdc_wallet_address: string | null;
  usdc_balance_cents: number;
  usdc_balance_usd: string;
  x402_total_calls: number;
  x402_total_spent_cents: number;
  x402_total_spent_usd: string;
  x402_last_call_at: number | null;
}

function Treasury() {
  const { currentTenant } = useTenant();
  const queryClient = useQueryClient();
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [activeTab, setActiveTab] = useState<"usd" | "usdc">("usd");

  // Fetch treasury data with demo fallback
  const { data, isLoading } = useQuery<{
    success: boolean;
    data: TreasuryData;
  }>({
    queryKey: ["treasury", currentTenant?.id],
    queryFn: async () => {
      try {
        const response = await fetch("/api/treasury", {
          headers: {
            "X-Tenant-ID": currentTenant?.id || "",
          },
        });
        const contentType = response.headers.get("content-type");
        if (
          !response.ok ||
          !contentType ||
          !contentType.includes("application/json")
        ) {
          // Return demo data
          return {
            success: true,
            data: getTenantDemoTreasury(currentTenant?.id || "arcteryx"),
          };
        }
        return response.json();
      } catch {
        // Return demo data on error
        return {
          success: true,
          data: getTenantDemoTreasury(currentTenant?.id || "arcteryx"),
        };
      }
    },
    enabled: !!currentTenant?.id,
  });

  // Deposit mutation
  const depositMutation = useMutation({
    mutationFn: async (depositData: {
      amount_cents: number;
      payment_method: string;
      payment_reference?: string;
      notes?: string;
    }) => {
      const response = await fetch("/api/treasury/deposits", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-ID": currentTenant?.id || "",
        },
        body: JSON.stringify(depositData),
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["treasury"] });
      setShowDepositModal(false);
    },
  });

  // Acknowledge alerts mutation
  const acknowledgeMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/treasury/alerts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-ID": currentTenant?.id || "",
        },
        body: JSON.stringify({ acknowledge_all: true }),
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["treasury"] });
    },
  });

  // USDC treasury data
  const { data: usdcData } = useQuery<UsdcTreasuryData>({
    queryKey: ["treasury-usdc", currentTenant?.id],
    queryFn: async () => {
      try {
        const response = await tenantFetch("/api/v1/treasury/usdc");
        if (!response.ok) throw new Error("Failed");
        return response.json();
      } catch {
        return {
          usdc_enabled: false,
          usdc_wallet_address: null,
          usdc_balance_cents: 0,
          usdc_balance_usd: "0.00",
          x402_total_calls: 0,
          x402_total_spent_cents: 0,
          x402_total_spent_usd: "0.00",
          x402_last_call_at: null,
        };
      }
    },
    enabled: !!currentTenant?.id,
  });

  // USDC enable/disable mutation
  const usdcToggleMutation = useMutation({
    mutationFn: async (action: "enable" | "disable") => {
      const response = await tenantFetch("/api/v1/treasury/usdc", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["treasury-usdc"] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-penta-grey-medium" size={24} />
      </div>
    );
  }

  // Use demo data if API call fails
  const treasury =
    data?.data || getTenantDemoTreasury(currentTenant?.id || "arcteryx");

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h3 text-penta-black">Treasury</h1>
          <p className="text-body text-penta-grey-medium mt-1">
            Manage funds for your takeback programs
          </p>
        </div>
        {activeTab === "usd" && (
          <button
            onClick={() => setShowDepositModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-penta-mint text-penta-black rounded-full font-mono text-sm uppercase tracking-wider"
          >
            <Plus size={16} />
            Add Funds
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-penta-grey-light">
        <button
          onClick={() => setActiveTab("usd")}
          className={`px-4 py-2 text-sm font-mono uppercase tracking-wider border-b-2 transition-colors ${
            activeTab === "usd"
              ? "border-penta-black text-penta-black"
              : "border-transparent text-penta-grey-medium hover:text-penta-black"
          }`}
        >
          <DollarSign size={14} className="inline mr-1" />
          USD
        </button>
        <button
          onClick={() => setActiveTab("usdc")}
          className={`px-4 py-2 text-sm font-mono uppercase tracking-wider border-b-2 transition-colors ${
            activeTab === "usdc"
              ? "border-penta-black text-penta-black"
              : "border-transparent text-penta-grey-medium hover:text-penta-black"
          }`}
        >
          <Zap size={14} className="inline mr-1" />
          USDC (x402)
        </button>
      </div>

      {/* USD Tab */}
      {activeTab === "usd" && (
        <>
          {/* Alerts */}
          {treasury.alerts.length > 0 && (
            <div className="bg-penta-coral/10 border border-penta-coral/20 p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <AlertTriangle
                    className="text-penta-coral mt-0.5"
                    size={20}
                  />
                  <div>
                    <p className="font-medium text-penta-black">
                      {treasury.alerts[0].message}
                    </p>
                    <p className="text-sm text-penta-grey-medium mt-1">
                      {treasury.alerts.length > 1 &&
                        `+${treasury.alerts.length - 1} more alerts`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => acknowledgeMutation.mutate()}
                  className="text-sm text-penta-coral hover:underline"
                >
                  Acknowledge all
                </button>
              </div>
            </div>
          )}

          {/* Balance Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Available Balance */}
            <div className="card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-penta-grey-medium">
                  Available Balance
                </span>
                <Wallet className="text-penta-mint" size={20} />
              </div>
              <p className="text-2xl font-light text-penta-black">
                {formatCurrency(treasury.balance.available_cents)}
              </p>
              {treasury.balance.reserved_cents > 0 && (
                <p className="text-xs text-penta-grey-medium mt-1">
                  {formatCurrency(treasury.balance.reserved_cents)} reserved
                </p>
              )}
            </div>

            {/* Total Balance */}
            <div className="card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-penta-grey-medium">
                  Total Balance
                </span>
                <DollarSign className="text-penta-blue" size={20} />
              </div>
              <p className="text-2xl font-light text-penta-black">
                {formatCurrency(treasury.balance.balance_cents)}
              </p>
              <p className="text-xs text-penta-grey-medium mt-1">
                Lifetime: {formatCurrency(treasury.balance.lifetime_deposited)}{" "}
                deposited
              </p>
            </div>

            {/* Spent */}
            <div className="card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-penta-grey-medium">
                  Spent on Takebacks
                </span>
                <TrendingDown className="text-penta-coral" size={20} />
              </div>
              <p className="text-2xl font-light text-penta-black">
                {formatCurrency(treasury.balance.lifetime_spent)}
              </p>
              <p className="text-xs text-penta-grey-medium mt-1">
                {formatCurrency(treasury.balance.deposits_last_30_days)}{" "}
                deposited last 30 days
              </p>
            </div>
          </div>

          {/* Recent Deposits & Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Deposits */}
            <div className="lg:col-span-2 card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-body font-medium text-penta-black">
                  Recent Deposits
                </h2>
                <button className="text-sm text-penta-mint hover:underline">
                  View all
                </button>
              </div>

              {treasury.recent_deposits.length === 0 ? (
                <div className="text-center py-8 text-penta-grey-medium">
                  <Wallet className="mx-auto mb-2" size={32} strokeWidth={1} />
                  <p>No deposits yet</p>
                  <button
                    onClick={() => setShowDepositModal(true)}
                    className="text-penta-mint hover:underline mt-2"
                  >
                    Add your first deposit
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {treasury.recent_deposits.map((deposit) => (
                    <div
                      key={deposit.id}
                      className="flex items-center justify-between p-3 bg-penta-grey-light/50"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            deposit.status === "completed"
                              ? "bg-penta-mint/20 text-penta-mint"
                              : deposit.status === "failed"
                                ? "bg-penta-coral/20 text-penta-coral"
                                : "bg-penta-grey-medium/20 text-penta-grey-medium"
                          }`}
                        >
                          {deposit.status === "completed" ? (
                            <Check size={16} />
                          ) : deposit.status === "failed" ? (
                            <X size={16} />
                          ) : (
                            <Clock size={16} />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-penta-black">
                            {formatCurrency(deposit.amount_cents)}
                          </p>
                          <p className="text-xs text-penta-grey-medium">
                            {deposit.payment_method.replace("_", " ")} •{" "}
                            {formatDateTime(deposit.created_at)}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          deposit.status === "completed"
                            ? "bg-penta-mint/20 text-penta-mint"
                            : deposit.status === "failed"
                              ? "bg-penta-coral/20 text-penta-coral"
                              : "bg-penta-grey-medium/20 text-penta-grey-medium"
                        }`}
                      >
                        {deposit.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions & Settings */}
            <div className="space-y-4">
              {/* Settings Card */}
              <div className="card">
                <h2 className="text-body font-medium text-penta-black mb-4">
                  Settings
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-penta-grey-light/50">
                    <div className="flex items-center gap-3">
                      <Bell size={18} className="text-penta-grey-medium" />
                      <div>
                        <p className="text-sm text-penta-black">
                          Low Balance Alert
                        </p>
                        <p className="text-xs text-penta-grey-medium">
                          At{" "}
                          {formatCurrency(
                            treasury.treasury.settings
                              .low_balance_threshold_cents,
                          )}
                        </p>
                      </div>
                    </div>
                    <ChevronRight
                      size={16}
                      className="text-penta-grey-medium"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-penta-grey-light/50">
                    <div className="flex items-center gap-3">
                      <RefreshCw size={18} className="text-penta-grey-medium" />
                      <div>
                        <p className="text-sm text-penta-black">Auto-Reload</p>
                        <p className="text-xs text-penta-grey-medium">
                          {treasury.treasury.settings.auto_reload_enabled
                            ? "Enabled"
                            : "Disabled"}
                        </p>
                      </div>
                    </div>
                    <ChevronRight
                      size={16}
                      className="text-penta-grey-medium"
                    />
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="card">
                <h2 className="text-body font-medium text-penta-black mb-4">
                  Activity
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-penta-grey-medium">
                      Pending Takebacks
                    </span>
                    <span className="text-sm font-medium text-penta-black">
                      {treasury.balance.pending_reservations}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-penta-grey-medium">
                      Reserved Funds
                    </span>
                    <span className="text-sm font-medium text-penta-black">
                      {formatCurrency(treasury.balance.reserved_cents)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* USDC Tab */}
      {activeTab === "usdc" && (
        <UsdcTab
          data={usdcData || null}
          onToggle={(action) => usdcToggleMutation.mutate(action)}
          isToggling={usdcToggleMutation.isPending}
        />
      )}

      {/* Deposit Modal */}
      <DepositModal
        isOpen={showDepositModal}
        onClose={() => setShowDepositModal(false)}
        onDeposit={(data) => depositMutation.mutate(data)}
        isLoading={depositMutation.isPending}
      />
    </div>
  );
}
